import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, MapPin, X } from "lucide-react";
import Layout, { Overline } from "../components/Layout";
import PageHero from "../components/PageHero";
import { Reveal } from "../components/Motion";
import { IMAGES, PROJECTS } from "../data/site";

const CATEGORIES = ["All", ...Array.from(new Set(PROJECTS.map((p) => p.category)))];

const CaseStudy = ({ p, onOpen, i }) => (
  <Reveal delay={(i % 2) * 0.08}>
    <button
      data-testid={`project-card-${p.slug}`}
      onClick={() => onOpen(p)}
      className="group block w-full text-left rounded-[2rem] overflow-hidden bg-charcoal relative h-[440px]"
    >
      <img src={p.image} alt={p.title} className="absolute inset-0 h-full w-full object-cover opacity-85 group-hover:opacity-100 group-hover:scale-105 transition-[transform,opacity] duration-700" />
      <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/40 to-transparent" />
      <div className="relative h-full p-8 flex flex-col justify-between text-warmwhite">
        <div className="flex items-center justify-between">
          <span className="text-xs tracking-overline uppercase bg-warmwhite/15 backdrop-blur px-3 py-1.5 rounded-full">{p.category}</span>
          <span className="inline-flex items-center gap-1 text-xs text-warmwhite/80"><MapPin size={12} /> {p.location}</span>
        </div>
        <div>
          <h3 className="font-heading text-3xl font-semibold tracking-tight">{p.title}</h3>
          <div className="mt-4 flex gap-6">
            {p.metrics.slice(0, 3).map((m) => (
              <div key={m.label}>
                <div className="font-heading text-2xl font-semibold text-sandstone">{m.value}</div>
                <div className="text-[11px] uppercase tracking-wide text-warmwhite/60">{m.label}</div>
              </div>
            ))}
          </div>
          <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-sandstone group-hover:gap-3 transition-all">Read case study <ArrowUpRight size={16} /></span>
        </div>
      </div>
    </button>
  </Reveal>
);

const Detail = ({ p, onClose }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-charcoal/70 backdrop-blur-sm p-4 sm:p-8" onClick={onClose}>
    <motion.div
      initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      onClick={(e) => e.stopPropagation()}
      className="relative w-full max-w-3xl bg-warmwhite rounded-[2rem] overflow-hidden my-4"
      data-testid="project-detail"
    >
      <button onClick={onClose} data-testid="project-detail-close" className="absolute top-5 right-5 z-10 h-11 w-11 rounded-full bg-warmwhite/90 backdrop-blur flex items-center justify-center text-charcoal hover:bg-white transition-colors"><X size={18} /></button>
      <div className="relative h-64">
        <img src={p.image} alt={p.title} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal/70 to-transparent" />
        <div className="absolute bottom-6 left-8 text-warmwhite">
          <span className="text-xs tracking-overline uppercase">{p.category} · {p.location}</span>
          <h3 className="font-heading text-3xl font-semibold tracking-tight mt-1">{p.title}</h3>
        </div>
      </div>
      <div className="p-8 lg:p-10">
        <div className="grid grid-cols-3 gap-4 mb-8">
          {p.metrics.map((m) => (
            <div key={m.label} className="rounded-2xl bg-sand p-5 text-center">
              <div className="font-heading text-2xl font-semibold text-eucalyptus">{m.value}</div>
              <div className="text-[11px] uppercase tracking-wide text-charcoal/60 mt-1">{m.label}</div>
            </div>
          ))}
        </div>
        <div className="space-y-6">
          {[["Challenge", p.challenge], ["Approach", p.approach], ["Outcome", p.outcome], ["Impact", p.impact]].map(([k, v]) => (
            <div key={k}>
              <p className="text-xs tracking-overline uppercase text-ochre mb-2">{k}</p>
              <p className="text-charcoal/80 leading-relaxed">{v}</p>
            </div>
          ))}
        </div>
        <Link to="/contact" onClick={onClose} data-testid="project-detail-cta" className="mt-8 inline-flex items-center gap-2 rounded-full bg-eucalyptus text-warmwhite px-6 py-3 font-semibold hover:bg-eucalyptus-deep transition-colors">
          Discuss a project like this <ArrowUpRight size={16} />
        </Link>
      </div>
    </motion.div>
  </motion.div>
);

export default function Projects() {
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);
  const list = filter === "All" ? PROJECTS : PROJECTS.filter((p) => p.category === filter);

  return (
    <Layout
      title="Projects"
      description="Case studies from Ranger Base — carbon readiness, protected-area planning, native seed enterprises, biodiversity monitoring, species inventories and cultural heritage field plans around the world."
    >
      <PageHero
        overline="Case Studies"
        titleLines={["Work that leaves", "Country stronger"]}
        subtitle="Real partnerships with Traditional Owner corporations. Every project is measured by the capability that remains."
        image={IMAGES.creek}
      />

      <section className="py-20 lg:py-28 bg-warmwhite">
        <div className="mx-auto max-w-[88rem] px-6 lg:px-10">
          <div className="flex flex-wrap gap-2 mb-12">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                data-testid={`project-filter-${c.toLowerCase().replace(/\s/g, "-")}`}
                onClick={() => setFilter(c)}
                className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-colors ${filter === c ? "bg-eucalyptus text-warmwhite" : "bg-charcoal/5 text-charcoal hover:bg-charcoal/10"}`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {list.map((p, i) => <CaseStudy key={p.slug} p={p} i={i} onOpen={setActive} />)}
          </div>
        </div>
      </section>

      <AnimatePresence>{active && <Detail p={active} onClose={() => setActive(null)} />}</AnimatePresence>
    </Layout>
  );
}
